/**
 * Access to the full Lucide icon set by *name*, for icons chosen by a user rather than
 * written into the code.
 *
 * Two different problems live behind one dataset:
 *
 * - Icons written literally in components (`<Play />`, `<Eye />`) should keep importing
 *   from `lucide-react`. A static import tree-shakes down to just that icon, so the ~137
 *   already in this codebase cost almost nothing. Do not route those through here.
 * - Icons whose name arrives at runtime — from a dashboard config, a category row, a map
 *   marker — cannot be tree-shaken, because a bundler has no way to know which of the 1848
 *   a database will produce. Those come from here.
 *
 * `lucide-static/icon-nodes.json` is the whole set as geometry (755KB raw, ~90KB gzipped).
 * It is measurably the cheapest way to ship every icon: the webfont is ~300KB on the wire
 * because woff2 stores *outlined* strokes and cannot be compressed further, and the SVG
 * sprite saves ~5KB but has to be hand-cache-busted as a `public/` asset instead of being
 * fingerprinted by Vite.
 *
 * Everything here is deliberately behind one module so the mechanism stays swappable: if
 * the 90KB ever has to become per-icon dynamic imports, or the sprite, only this file and
 * `dynamic-icon.tsx` change — no call site does.
 */

/** One SVG element of an icon: `["path", { d: "..." }]`. */
export type IconNode = [
  tag: string,
  attributes: Record<string, string | number>,
];

export type IconNodeMap = Record<string, IconNode[]>;

/** Rendered when a stored name resolves to nothing — see `resolveIconNodes`. */
export const FALLBACK_ICON_NAME = "squircle";

/** Lucide's own canvas and stroke, which every icon's geometry is drawn against. */
export const ICON_VIEW_BOX = "0 0 24 24";
export const ICON_STROKE_WIDTH = 2;

/**
 * The colour a user-chosen icon starts as, wherever one can be picked.
 *
 * Lives here rather than beside any one picker because four places needed it — the form
 * field, the widget config field, the category form and the nav widget — and four copies
 * of a hex string drift the moment one of them is adjusted.
 */
export const DEFAULT_ICON_COLOR = "#2196F3";
export const DEFAULT_DASHBOARD_ICON = "layout-dashboard";

let loaded: IconNodeMap | null = null;
let loading: Promise<IconNodeMap> | null = null;

/**
 * Loads the icon geometry, once per session.
 *
 * Dynamically imported so the 90KB lands in its own chunk rather than the entry bundle —
 * a dashboard with no user-chosen icons never pays for it. The promise is cached, so a
 * page rendering forty markers triggers one fetch, not forty. A failed load is not cached:
 * the promise is dropped so the next caller retries instead of reusing the rejection.
 */
export function loadIconNodes(): Promise<IconNodeMap> {
  if (loaded) return Promise.resolve(loaded);

  loading ??= import("lucide-static/icon-nodes.json")
    .then((module) => {
      loaded = (module.default ?? module) as unknown as IconNodeMap;
      return loaded;
    })
    .catch((error) => {
      loading = null;
      throw error;
    });

  return loading;
}

/** The already-loaded set, or null. For synchronous callers such as Leaflet's `divIcon`,
 * which builds markup in a callback that cannot await. */
export function getLoadedIconNodes(): IconNodeMap | null {
  return loaded;
}

/** `{ "trash-off": ["empty", "cleanup", "clear", ...] }` — Lucide's own search index. */
export type IconTagMap = Record<string, string[]>;

let loadedTags: IconTagMap | null = null;
let loadingTags: Promise<IconTagMap> | null = null;

/**
 * Loads the search tags, which only the picker ever needs.
 *
 * Deliberately a second chunk rather than part of the geometry: tags are what make
 * searching "clear" return the trash icons, but they are never needed to *draw* anything.
 * Keeping them separate means a dashboard that renders forty icons pays ~90KB, not the
 * ~136KB it would if the index rode along.
 */
export function loadIconTags(): Promise<IconTagMap> {
  if (loadedTags) return Promise.resolve(loadedTags);

  // Not cached on failure either; see `loadIconNodes`.
  loadingTags ??= import("lucide-static/tags.json")
    .then((module) => {
      loadedTags = (module.default ?? module) as unknown as IconTagMap;
      return loadedTags;
    })
    .catch((error) => {
      loadingTags = null;
      throw error;
    });

  return loadingTags;
}

export function getLoadedIconTags(): IconTagMap | null {
  return loadedTags;
}

/**
 * Ranks icons against a search term, closest match first.
 *
 * Ranked rather than filtered because Lucide's tags are generous: "clear" matches 24
 * icons, and an arbitrary order buries `eraser` among a dozen trash cans. The tiers are
 * name-before-tag and whole-before-partial, so typing an exact icon name always puts it
 * first even when a hundred icons carry it as a tag.
 *
 * An empty term returns everything in the dataset's own order, which is roughly
 * alphabetical — the same thing lucide.dev shows before you type.
 */
export function searchIcons(
  names: string[],
  tags: IconTagMap | null,
  query: string,
): string[] {
  const term = query.trim().toLowerCase();
  if (!term) return names;

  const scored: { name: string; score: number }[] = [];

  for (const name of names) {
    let score = 0;

    if (name === term) score = 6;
    else if (name.startsWith(term)) score = 5;
    else if (name.includes(term)) score = 4;

    if (score === 0 && tags) {
      const iconTags = tags[name];
      if (iconTags) {
        if (iconTags.some((tag) => tag === term)) score = 3;
        else if (iconTags.some((tag) => tag.startsWith(term))) score = 2;
        else if (iconTags.some((tag) => tag.includes(term))) score = 1;
      }
    }

    if (score > 0) scored.push({ name, score });
  }

  // Ties keep the dataset's order, so results don't reshuffle as you type another letter.
  return scored.sort((a, b) => b.score - a.score).map((entry) => entry.name);
}

/**
 * Tolerates a stored name that isn't quite a Lucide id.
 *
 * Lucide's ids are kebab-case (`circle-check`) and that is what we store, but names have
 * historically been written as camelCase locals — `value-widget.tsx` still holds a set of
 * them. Converting `cloudRain` to `cloud-rain` costs three lines and rescues the cases
 * where the camelCase name happened to match; the ones that don't (`checkCircle` is
 * Lucide's `circle-check`, not `check-circle`) fall through to the fallback icon rather
 * than throwing.
 */
export function normalizeIconName(name: string): string {
  return name
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .toLowerCase();
}

/**
 * Geometry for a name, or the fallback icon's geometry if it resolves to nothing.
 *
 * Never returns null for a loaded set and never throws. The name comes from stored data
 * and Lucide renames icons between releases, so an unresolvable name is an expected state,
 * not an exceptional one — a dashboard saved two years ago should render a placeholder,
 * not an error card.
 */
export function resolveIconNodes(
  icons: IconNodeMap,
  name: string | null | undefined,
): IconNode[] | null {
  if (!name) return null;

  const exact = ownIcon(icons, name) ?? ownIcon(icons, normalizeIconName(name));
  return exact ?? ownIcon(icons, FALLBACK_ICON_NAME) ?? null;
}

/** The map is a plain object, so a stored name like "constructor" or "__proto__" would
 * otherwise resolve to an inherited member rather than to nothing. */
function ownIcon(icons: IconNodeMap, name: string): IconNode[] | undefined {
  return Object.hasOwn(icons, name) ? icons[name] : undefined;
}

/** Whether a name is a real Lucide icon, as opposed to one that would render the
 * fallback. For the picker and for config validation. */
export function isKnownIconName(
  icons: IconNodeMap,
  name: string | null | undefined,
): boolean {
  if (!name) return false;
  return Object.hasOwn(icons, name) || Object.hasOwn(icons, normalizeIconName(name));
}

const ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};

const escapeAttribute = (value: string | number) =>
  String(value).replace(/[&<>"]/g, (char) => ESCAPES[char]);

/**
 * An icon as an SVG *string*, for the places that build HTML rather than React.
 *
 * Leaflet is the reason this exists: `createIcon` in `node-map-leaflet-util.ts` assembles
 * a `divIcon` from an HTML template, and the future image-marker widget will do the same.
 * Handing those a React element would mean `renderToString` and pulling in
 * `react-dom/server`; handing them geometry means interpolating a string.
 *
 * Returns "" when the set hasn't loaded yet — callers await `loadIconNodes()` first. The
 * colour is escaped because it comes from a user's config; the geometry isn't user input,
 * but it goes through the same path so nothing has to remember which is which.
 */
export function iconToSvgMarkup(
  name: string | null | undefined,
  {
    size = 24,
    color = "currentColor",
    strokeWidth = ICON_STROKE_WIDTH,
  }: { size?: number; color?: string; strokeWidth?: number } = {},
): string {
  const icons = getLoadedIconNodes();
  if (!icons) return "";

  const nodes = resolveIconNodes(icons, name);
  if (!nodes) return "";

  const children = nodes
    .map(([tag, attributes]) => {
      const rendered = Object.entries(attributes)
        .map(([key, value]) => `${key}="${escapeAttribute(value)}"`)
        .join(" ");
      return `<${tag} ${rendered}/>`;
    })
    .join("");

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" ` +
    `viewBox="${ICON_VIEW_BOX}" fill="none" stroke="${escapeAttribute(color)}" ` +
    `stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round">` +
    `${children}</svg>`
  );
}
